import React, { useEffect, useState, useCallback } from "react";
import { fmtDate } from "@/lib/dt";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Bell, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

const TYPE_ICON = {
  job_done: "✅",
  job_failed: "❌",
  platform_alert: "🚨",   // STEP 10 · E: an automatic Founder alert
  signoff: "🔒",
  unlock: "🔓",
};

function timeAgo(iso) {
  try {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return fmtDate(d);
  } catch { return ""; }
}

export default function NotificationsBell() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/notifications", { _silent: true });
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);

  const markAll = async () => {
    try {
      await api.post("/notifications/mark-read");
      setUnread(0);
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
    } catch { /* silent */ }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && unread > 0) markAll(); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative"
                aria-label={t("notifications.title")} data-testid="notifications-bell">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -end-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-bold text-white"
                  data-testid="notifications-unread-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <div className="text-sm font-semibold">{t("notifications.title")}</div>
          {items.length > 0 && (
            <button onClick={markAll}
                    className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800"
                    data-testid="notifications-mark-read">
              <CheckCheck className="h-3 w-3" /> {t("notifications.markAllRead")}
            </button>
          )}
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {items.length === 0 && (
            <div className="p-6 text-center text-xs text-muted-foreground">
              {t("notifications.empty")}
            </div>
          )}
          {items.map((n) => (
            <div key={n.id}
                 className={`border-b px-3 py-2 text-sm ${n.read ? "" : "bg-emerald-50/50 dark:bg-emerald-950/20"}`}
                 data-testid={`notification-${n.id}`}>
              <div className="flex items-start gap-2">
                <div className="text-base">{TYPE_ICON[n.type] || "🔔"}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium break-words">{n.link
                    ? <a href={n.link} className="hover:underline" data-testid="notification-link">{(lang === "ar" && n.title_ar) || n.title}</a>   /* STEP 10 · E: an alert opens its Founder tab */
                    : ((lang === "ar" && n.title_ar) || n.title)}</div>{/* STEP 7e-2: the Arabic a row carries */}
                  <div className="text-xs text-muted-foreground break-words">{(lang === "ar" && n.message_ar) || n.message}</div>
                  <div className="mt-0.5 text-[10px] text-muted-foreground">{timeAgo(n.created_at)}</div>
                </div>
                {!n.read && (
                  <span className="mt-1 h-2 w-2 rounded-full bg-emerald-500" aria-label="unread" />
                )}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
